import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  Sparkles, Image as ImageIcon, Video, FileText, Download, Copy, 
  RefreshCw, Check, X, Play, Pause, Calendar, Send, 
  Wand2, Layers, Eye, Settings2, Clock, ArrowRight,
  ChevronDown, ChevronUp, Trash2, Plus, Upload, Star,
  Zap, Target, TrendingUp, Share2, Instagram, Facebook,
  Linkedin, Twitter, Mail, MessageSquare, FlaskConical,
  BarChart3, Trophy, AlertCircle, Percent
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/campaign-creator`;

// AI Provider Icons Component
const ProviderIcon = ({ provider }) => {
  const icons = {
    'gpt-5.2': '🧠',
    'claude-sonnet': '🤖',
    'gemini-flash': '✨',
    'gpt-image-1': '🎨',
    'nano-banana': '🍌',
    'sora-2': '🎬',
    'kling': '🎥',
    'seedance': '💃'
  };
  return <span className="text-lg">{icons[provider] || '🔮'}</span>;
};

// Platform Icons
const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  const icons = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    twitter: Twitter,
    email: Mail,
    whatsapp: MessageSquare
  };
  const Icon = icons[platform] || Share2;
  return <Icon className={className} />;
};

// Content Comparison Card
const ComparisonCard = ({ result, type, onSelect, isSelected }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!result.success) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-red-600">
            <X className="w-4 h-4" />
            <span className="text-sm">{result.error || 'Generation failed'}</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-accent border-accent' : 'border-slate-200'
      }`}
      onClick={() => onSelect(result)}
      data-testid={`comparison-card-${result.provider_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ProviderIcon provider={result.provider_id} />
            <CardTitle className="text-sm font-medium">{result.provider}</CardTitle>
          </div>
          {isSelected && (
            <Badge className="bg-accent text-white">
              <Check className="w-3 h-3 mr-1" /> Selected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {type === 'text' && result.content && (
          <div className="space-y-2">
            {result.content.headline && (
              <p className="font-semibold text-sm text-primary line-clamp-2">
                {result.content.headline}
              </p>
            )}
            <p className={`text-sm text-slate-600 ${expanded ? '' : 'line-clamp-3'}`}>
              {result.content.body || result.content.text || JSON.stringify(result.content)}
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="text-xs"
            >
              {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          </div>
        )}
        
        {type === 'image' && result.image_base64 && (
          <div className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
            <img 
              src={`data:image/png;base64,${result.image_base64}`}
              alt={`Generated by ${result.provider}`}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {type === 'video' && (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-900">
            {result.video_base64 ? (
              <video 
                src={`data:video/mp4;base64,${result.video_base64}`}
                controls
                className="w-full h-full"
              />
            ) : result.video_url ? (
              <video 
                src={result.video_url}
                controls
                className="w-full h-full"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-white">
                <Play className="w-8 h-8" />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Campaign Brief Form Component
const CampaignBriefForm = ({ brief, setBrief, onNext }) => {
  const campaignTypes = [
    { value: 'social_media', label: 'Social Media Campaign' },
    { value: 'email_sequence', label: 'Email Sequence' },
    { value: 'ad_campaign', label: 'Ad Campaign' },
    { value: 'product_launch', label: 'Product Launch' },
    { value: 'brand_awareness', label: 'Brand Awareness' },
    { value: 'lead_generation', label: 'Lead Generation' }
  ];
  
  const industries = [
    { value: 'saas', label: 'SaaS / Technology' },
    { value: 'ecommerce', label: 'E-commerce / Retail' },
    { value: 'finance', label: 'Finance / Banking' },
    { value: 'healthcare', label: 'Healthcare / Wellness' },
    { value: 'education', label: 'Education / Training' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'hospitality', label: 'Hospitality / Travel' }
  ];
  
  const tones = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual & Friendly' },
    { value: 'luxury', label: 'Luxury & Premium' },
    { value: 'playful', label: 'Playful & Fun' },
    { value: 'urgent', label: 'Urgent & Action-Oriented' }
  ];
  
  const platforms = [
    { value: 'instagram', label: 'Instagram', icon: Instagram },
    { value: 'facebook', label: 'Facebook', icon: Facebook },
    { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { value: 'twitter', label: 'Twitter/X', icon: Twitter },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare }
  ];
  
  const isValid = brief.product && brief.goal && brief.audience;
  
  return (
    <div className="space-y-6" data-testid="campaign-brief-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="campaign_type">Campaign Type</Label>
          <Select
            value={brief.campaign_type || 'social_media'}
            onValueChange={(value) => setBrief({ ...brief, campaign_type: value })}
          >
            <SelectTrigger data-testid="campaign-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {campaignTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="industry">Industry</Label>
          <Select
            value={brief.industry || 'saas'}
            onValueChange={(value) => setBrief({ ...brief, industry: value })}
          >
            <SelectTrigger data-testid="industry-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>
                  {ind.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label htmlFor="product">Product/Service Name *</Label>
        <Input
          id="product"
          value={brief.product || ''}
          onChange={(e) => setBrief({ ...brief, product: e.target.value })}
          placeholder="e.g., Treppan CRM - Enterprise Sales Platform"
          data-testid="product-input"
        />
      </div>
      
      <div>
        <Label htmlFor="audience">Target Audience *</Label>
        <Input
          id="audience"
          value={brief.audience || ''}
          onChange={(e) => setBrief({ ...brief, audience: e.target.value })}
          placeholder="e.g., Sales managers and business owners in B2B companies"
          data-testid="audience-input"
        />
      </div>
      
      <div>
        <Label htmlFor="goal">Campaign Goal *</Label>
        <Textarea
          id="goal"
          value={brief.goal || ''}
          onChange={(e) => setBrief({ ...brief, goal: e.target.value })}
          placeholder="e.g., Drive sign-ups for free trial, increase brand awareness, promote new feature launch"
          rows={3}
          data-testid="goal-input"
        />
      </div>
      
      <div>
        <Label htmlFor="key_messages">Key Messages (Optional)</Label>
        <Textarea
          id="key_messages"
          value={brief.key_messages || ''}
          onChange={(e) => setBrief({ ...brief, key_messages: e.target.value })}
          placeholder="Main points you want to communicate..."
          rows={2}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="tone">Content Tone</Label>
          <Select
            value={brief.tone || 'professional'}
            onValueChange={(value) => setBrief({ ...brief, tone: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tones.map((tone) => (
                <SelectItem key={tone.value} value={tone.value}>
                  {tone.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="platform">Primary Platform</Label>
          <Select
            value={brief.platform || 'instagram'}
            onValueChange={(value) => setBrief({ ...brief, platform: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {platforms.map((plat) => (
                <SelectItem key={plat.value} value={plat.value}>
                  <div className="flex items-center gap-2">
                    <plat.icon className="w-4 h-4" />
                    {plat.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Button
        onClick={onNext}
        disabled={!isValid}
        className="w-full bg-accent hover:bg-accent/90"
        size="lg"
        data-testid="continue-to-generation-btn"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        Continue to AI Generation
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

// AI Provider Selection Component
const ProviderSelector = ({ type, selected, onSelect, providers }) => {
  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Select AI Providers to Compare</Label>
      <div className="flex flex-wrap gap-2">
        {providers.map((provider) => (
          <Button
            key={provider.id}
            variant={selected.includes(provider.id) ? "default" : "outline"}
            size="sm"
            onClick={() => {
              if (selected.includes(provider.id)) {
                onSelect(selected.filter(p => p !== provider.id));
              } else {
                onSelect([...selected, provider.id]);
              }
            }}
            className={selected.includes(provider.id) ? "bg-accent" : ""}
            data-testid={`provider-btn-${provider.id}`}
          >
            <ProviderIcon provider={provider.id} />
            <span className="ml-2">{provider.name}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

// Main Component
const AICampaignCreator360 = () => {
  const { token } = useAuth();
  
  // State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({ text: [], image: [], video: [] });
  
  // Campaign Brief
  const [brief, setBrief] = useState({
    campaign_type: 'social_media',
    industry: 'saas',
    product: '',
    audience: '',
    goal: '',
    key_messages: '',
    tone: 'professional',
    platform: 'instagram'
  });
  
  // Selected Providers
  const [selectedTextProviders, setSelectedTextProviders] = useState(['gpt-5.2']);
  const [selectedImageProviders, setSelectedImageProviders] = useState(['gpt-image-1']);
  const [selectedVideoProviders, setSelectedVideoProviders] = useState([]);
  
  // Generated Content
  const [textResults, setTextResults] = useState([]);
  const [imageResults, setImageResults] = useState([]);
  const [videoResults, setVideoResults] = useState([]);
  
  // Selected Content
  const [selectedText, setSelectedText] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  // Generation Progress
  const [generationProgress, setGenerationProgress] = useState({ text: 0, image: 0, video: 0 });
  const [generatingType, setGeneratingType] = useState(null);
  
  // Saved Campaigns
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // A/B Testing State
  const [abTests, setAbTests] = useState([]);
  const [showABTestDialog, setShowABTestDialog] = useState(false);
  const [abTestName, setAbTestName] = useState('');
  const [abTestGoal, setAbTestGoal] = useState('clicks');
  const [abTestVariants, setAbTestVariants] = useState([]);
  const [trafficSplit, setTrafficSplit] = useState([50, 50]);
  const [selectedABTest, setSelectedABTest] = useState(null);
  const [showABTestAnalytics, setShowABTestAnalytics] = useState(false);
  
  // Fetch available providers
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${API}/providers?token=${token}`);
        if (response.data.success) {
          setProviders({
            text: response.data.text || [],
            image: response.data.image || [],
            video: response.data.video || []
          });
        }
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        // Fallback providers
        setProviders({
          text: [
            { id: 'gpt-5.2', name: 'GPT-5.2' },
            { id: 'claude-sonnet', name: 'Claude Sonnet 4.5' },
            { id: 'gemini-flash', name: 'Gemini 3 Flash' }
          ],
          image: [
            { id: 'gpt-image-1', name: 'GPT Image 1' },
            { id: 'nano-banana', name: 'Gemini Nano Banana' }
          ],
          video: [
            { id: 'sora-2', name: 'Sora 2' },
            { id: 'kling', name: 'Kling AI' },
            { id: 'seedance', name: 'Seedance' }
          ]
        });
      }
    };
    
    if (token) {
      fetchProviders();
    }
  }, [token]);
  
  // Fetch saved campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await axios.get(`${API}/campaigns?token=${token}&limit=10`);
        if (response.data.success) {
          setSavedCampaigns(response.data.campaigns || []);
        }
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
      }
    };
    
    if (token) {
      fetchCampaigns();
    }
  }, [token]);
  
  // Fetch A/B tests
  useEffect(() => {
    const fetchABTests = async () => {
      try {
        const response = await axios.get(`${API}/ab-tests?token=${token}&limit=20`);
        if (response.data.success) {
          setAbTests(response.data.tests || []);
        }
      } catch (error) {
        console.error('Failed to fetch A/B tests:', error);
      }
    };
    
    if (token) {
      fetchABTests();
    }
  }, [token]);
  
  // Generate Text Content
  const generateText = async () => {
    if (selectedTextProviders.length === 0) {
      toast.error('Please select at least one text AI provider');
      return;
    }
    
    setLoading(true);
    setGeneratingType('text');
    setGenerationProgress(prev => ({ ...prev, text: 10 }));
    
    try {
      const response = await axios.post(
        `${API}/generate/text/compare?token=${token}`,
        {
          prompt: `Create compelling marketing content for: ${brief.product}. 
Target audience: ${brief.audience}. 
Campaign goal: ${brief.goal}. 
Tone: ${brief.tone}.
Platform: ${brief.platform}.
${brief.key_messages ? `Key messages: ${brief.key_messages}` : ''}`,
          provider_ids: selectedTextProviders,
          context: brief
        }
      );
      
      setGenerationProgress(prev => ({ ...prev, text: 100 }));
      
      if (response.data.success) {
        setTextResults(response.data.comparisons || []);
        toast.success('Text content generated!');
      }
    } catch (error) {
      console.error('Text generation error:', error);
      toast.error('Failed to generate text content');
    } finally {
      setLoading(false);
      setGeneratingType(null);
    }
  };
  
  // Generate Images
  const generateImages = async () => {
    if (selectedImageProviders.length === 0) {
      toast.error('Please select at least one image AI provider');
      return;
    }
    
    setLoading(true);
    setGeneratingType('image');
    setGenerationProgress(prev => ({ ...prev, image: 10 }));
    
    try {
      const response = await axios.post(
        `${API}/generate/image/compare?token=${token}`,
        {
          prompt: `Create a professional marketing image for ${brief.industry} industry.
Product: ${brief.product}
Campaign: ${brief.goal}
Style: ${brief.tone}, eye-catching, suitable for ${brief.platform}
Requirements: High quality, professional, brand-safe, no text overlays`,
          provider_ids: selectedImageProviders,
          size: '1024x1024'
        }
      );
      
      setGenerationProgress(prev => ({ ...prev, image: 100 }));
      
      if (response.data.success) {
        setImageResults(response.data.comparisons || []);
        toast.success('Images generated!');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error('Failed to generate images');
    } finally {
      setLoading(false);
      setGeneratingType(null);
    }
  };
  
  // Generate Videos
  const generateVideos = async () => {
    if (selectedVideoProviders.length === 0) {
      toast.error('Please select at least one video AI provider');
      return;
    }
    
    setLoading(true);
    setGeneratingType('video');
    setGenerationProgress(prev => ({ ...prev, video: 10 }));
    toast.info('Video generation may take 2-5 minutes...');
    
    try {
      const response = await axios.post(
        `${API}/generate/video/compare?token=${token}`,
        {
          prompt: `Create a short marketing video for ${brief.product}.
Target audience: ${brief.audience}
Campaign goal: ${brief.goal}
Style: ${brief.tone}, engaging, dynamic
The video should be visually captivating and convey the brand message.`,
          provider_ids: selectedVideoProviders,
          duration: 4
        },
        { timeout: 600000 } // 10 minute timeout for video
      );
      
      setGenerationProgress(prev => ({ ...prev, video: 100 }));
      
      if (response.data.success) {
        setVideoResults(response.data.comparisons || []);
        toast.success('Videos generated!');
      }
    } catch (error) {
      console.error('Video generation error:', error);
      toast.error('Failed to generate videos. This may take longer - try again.');
    } finally {
      setLoading(false);
      setGeneratingType(null);
    }
  };
  
  // Save Campaign
  const saveCampaign = async () => {
    try {
      const campaignData = {
        brief,
        text_content: selectedText,
        image: selectedImage,
        video: selectedVideo,
        status: 'draft',
        name: `Campaign for ${brief.product}`
      };
      
      const response = await axios.post(
        `${API}/campaigns?token=${token}`,
        campaignData
      );
      
      if (response.data.success) {
        toast.success('Campaign saved!');
        setSavedCampaigns(prev => [response.data.campaign, ...prev]);
      }
    } catch (error) {
      console.error('Save campaign error:', error);
      toast.error('Failed to save campaign');
    }
  };
  
  // Copy content to clipboard
  const copyContent = () => {
    if (!selectedText?.content) return;
    
    const content = selectedText.content;
    const fullText = `${content.headline || ''}\n\n${content.body || content.text || ''}\n\n${content.cta || ''}\n\n${(content.hashtags || []).join(' ')}`;
    
    navigator.clipboard.writeText(fullText);
    toast.success('Content copied to clipboard!');
  };
  
  // Download image
  const downloadImage = () => {
    if (!selectedImage?.image_base64) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedImage.image_base64}`;
    link.download = `campaign-${brief.product.replace(/\s+/g, '-')}-${Date.now()}.png`;
    link.click();
    toast.success('Image downloaded!');
  };
  
  // Enhance existing content
  const enhanceContent = async (enhancementType) => {
    if (!selectedText?.content) {
      toast.error('Please select content to enhance first');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/enhance/text?token=${token}`,
        {
          content: selectedText.content.body || JSON.stringify(selectedText.content),
          type: enhancementType,
          provider_id: 'gpt-5.2'
        }
      );
      
      if (response.data.success) {
        setTextResults(prev => [
          {
            success: true,
            provider: `Enhanced (${enhancementType})`,
            provider_id: 'enhanced',
            content: { body: response.data.enhanced, ...selectedText.content }
          },
          ...prev
        ]);
        toast.success('Content enhanced!');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      toast.error('Failed to enhance content');
    } finally {
      setLoading(false);
    }
  };
  
  // Import previous campaign
  const importCampaign = (campaign) => {
    if (campaign.brief) {
      setBrief(campaign.brief);
    }
    if (campaign.text_content) {
      setSelectedText(campaign.text_content);
      setTextResults([campaign.text_content]);
    }
    if (campaign.image) {
      setSelectedImage(campaign.image);
      setImageResults([campaign.image]);
    }
    setShowImportDialog(false);
    setStep(2);
    toast.success('Campaign imported! You can now enhance or regenerate content.');
  };
  
  // ==================== A/B TESTING FUNCTIONS ====================
  
  // Create A/B Test
  const createABTest = async () => {
    if (!abTestName || abTestVariants.length < 2) {
      toast.error('Please provide a test name and select at least 2 variants');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/ab-tests?token=${token}`,
        {
          name: abTestName,
          campaign_id: `campaign_${Date.now()}`,
          variants: abTestVariants.map((v, i) => ({
            name: `Variant ${String.fromCharCode(65 + i)}`,
            content: v.content,
            provider: v.provider
          })),
          platform: brief.platform,
          goal: abTestGoal,
          traffic_split: trafficSplit
        }
      );
      
      if (response.data.success) {
        setAbTests(prev => [response.data.ab_test, ...prev]);
        setShowABTestDialog(false);
        setAbTestName('');
        setAbTestVariants([]);
        setTrafficSplit([50, 50]);
        toast.success('A/B test created! Start it when ready.');
      }
    } catch (error) {
      console.error('Create A/B test error:', error);
      toast.error('Failed to create A/B test');
    } finally {
      setLoading(false);
    }
  };
  
  // Start A/B Test
  const startABTest = async (testId) => {
    try {
      const response = await axios.post(`${API}/ab-tests/${testId}/start?token=${token}`);
      if (response.data.success) {
        setAbTests(prev => prev.map(t => 
          t.test_id === testId ? { ...t, status: 'running' } : t
        ));
        toast.success('A/B test started!');
      }
    } catch (error) {
      console.error('Start A/B test error:', error);
      toast.error('Failed to start test');
    }
  };
  
  // Pause A/B Test
  const pauseABTest = async (testId) => {
    try {
      const response = await axios.post(`${API}/ab-tests/${testId}/pause?token=${token}`);
      if (response.data.success) {
        setAbTests(prev => prev.map(t => 
          t.test_id === testId ? { ...t, status: 'paused' } : t
        ));
        toast.success('A/B test paused');
      }
    } catch (error) {
      console.error('Pause A/B test error:', error);
      toast.error('Failed to pause test');
    }
  };
  
  // End A/B Test
  const endABTest = async (testId, winnerId = null) => {
    try {
      const response = await axios.post(
        `${API}/ab-tests/${testId}/end?token=${token}`,
        { winner_variant_id: winnerId }
      );
      if (response.data.success) {
        setAbTests(prev => prev.map(t => 
          t.test_id === testId ? { ...t, status: 'completed', winner: response.data.winner } : t
        ));
        toast.success(`A/B test completed! Winner: ${response.data.winner || 'Auto-detected'}`);
      }
    } catch (error) {
      console.error('End A/B test error:', error);
      toast.error('Failed to end test');
    }
  };
  
  // Delete A/B Test
  const deleteABTest = async (testId) => {
    try {
      const response = await axios.delete(`${API}/ab-tests/${testId}?token=${token}`);
      if (response.data.success) {
        setAbTests(prev => prev.filter(t => t.test_id !== testId));
        toast.success('A/B test deleted');
      }
    } catch (error) {
      console.error('Delete A/B test error:', error);
      toast.error('Failed to delete test');
    }
  };
  
  // View A/B Test Analytics
  const viewABTestAnalytics = async (testId) => {
    try {
      const response = await axios.get(`${API}/ab-tests/${testId}/analytics?token=${token}`);
      if (response.data.success) {
        setSelectedABTest(response.data);
        setShowABTestAnalytics(true);
      }
    } catch (error) {
      console.error('Get A/B analytics error:', error);
      toast.error('Failed to load analytics');
    }
  };
  
  // Add variant to A/B test
  const addVariantToABTest = (result, type) => {
    if (abTestVariants.length >= 4) {
      toast.error('Maximum 4 variants allowed');
      return;
    }
    
    const variant = {
      type,
      content: result.content || result,
      provider: result.provider,
      provider_id: result.provider_id
    };
    
    setAbTestVariants(prev => [...prev, variant]);
    
    // Update traffic split evenly
    const newCount = abTestVariants.length + 1;
    const evenSplit = Math.floor(100 / newCount);
    const remainder = 100 - (evenSplit * newCount);
    const newSplit = Array(newCount).fill(evenSplit);
    newSplit[0] += remainder;
    setTrafficSplit(newSplit);
    
    toast.success(`Added ${result.provider} to A/B test variants`);
  };
  
  // Remove variant from A/B test
  const removeVariantFromABTest = (index) => {
    setAbTestVariants(prev => prev.filter((_, i) => i !== index));
    
    // Update traffic split
    const newCount = abTestVariants.length - 1;
    if (newCount > 0) {
      const evenSplit = Math.floor(100 / newCount);
      const remainder = 100 - (evenSplit * newCount);
      const newSplit = Array(newCount).fill(evenSplit);
      newSplit[0] += remainder;
      setTrafficSplit(newSplit);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="ai-campaign-creator-360">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-heading font-bold text-primary">
            360° AI Campaign Creator
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Generate, compare, and publish campaign content with multiple AI providers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowImportDialog(true)}
            data-testid="import-campaign-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Badge className="bg-accent text-white text-sm px-3 py-1">
            <Sparkles size={14} className="mr-1" />
            Multi-AI Powered
          </Badge>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 md:gap-4 py-4 flex-wrap">
        {[
          { num: 1, label: 'Brief', icon: Target },
          { num: 2, label: 'Generate', icon: Sparkles },
          { num: 3, label: 'Review', icon: Eye },
          { num: 4, label: 'A/B Test', icon: FlaskConical }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <button
              onClick={() => {
                if (s.num <= 3 || (s.num === 4 && (textResults.length > 0 || imageResults.length > 0))) {
                  setStep(s.num);
                }
              }}
              disabled={s.num === 4 && textResults.length === 0 && imageResults.length === 0}
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                step >= s.num 
                  ? 'bg-accent text-white' 
                  : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
              } ${s.num === 4 && textResults.length === 0 && imageResults.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {step > s.num ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
            </button>
            <span className={`ml-2 text-sm hidden sm:inline ${step >= s.num ? 'text-primary font-medium' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {i < 3 && <div className={`w-6 md:w-12 h-0.5 mx-2 md:mx-4 ${step > s.num ? 'bg-accent' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Campaign Brief */}
      {step === 1 && (
        <Card data-testid="campaign-brief-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-accent" />
              Campaign Brief
            </CardTitle>
            <CardDescription>
              Tell us about your campaign and we'll generate content across multiple AI providers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CampaignBriefForm 
              brief={brief} 
              setBrief={setBrief} 
              onNext={() => setStep(2)} 
            />
          </CardContent>
        </Card>
      )}
      
      {/* Step 2: Generate Content */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setStep(1)}
              variant="outline"
              data-testid="back-to-brief-btn"
            >
              ← Back to Brief
            </Button>
            <div className="flex-1">
              <h4 className="font-medium text-primary">{brief.product}</h4>
              <p className="text-sm text-slate-500">{brief.campaign_type} • {brief.platform}</p>
            </div>
            <Button
              onClick={() => setStep(3)}
              disabled={!selectedText && !selectedImage && !selectedVideo}
              className="bg-accent hover:bg-accent/90"
              data-testid="continue-to-review-btn"
            >
              Continue to Review
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          <Tabs defaultValue="text" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="text" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Text/Copy
                {textResults.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{textResults.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="image" className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Graphics
                {imageResults.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{imageResults.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="video" className="flex items-center gap-2">
                <Video className="w-4 h-4" />
                Videos
                {videoResults.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{videoResults.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            {/* Text Generation Tab */}
            <TabsContent value="text" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Text Content Generation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ProviderSelector
                    type="text"
                    selected={selectedTextProviders}
                    onSelect={setSelectedTextProviders}
                    providers={providers.text}
                  />
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={generateText}
                      disabled={loading || selectedTextProviders.length === 0}
                      className="bg-accent hover:bg-accent/90"
                      data-testid="generate-text-btn"
                    >
                      {generatingType === 'text' ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Text
                        </>
                      )}
                    </Button>
                    
                    {textResults.length > 0 && (
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => enhanceContent('improve')}
                          disabled={!selectedText || loading}
                        >
                          <Wand2 className="w-4 h-4 mr-1" />
                          Improve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => enhanceContent('shorten')}
                          disabled={!selectedText || loading}
                        >
                          Shorten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => enhanceContent('expand')}
                          disabled={!selectedText || loading}
                        >
                          Expand
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {generatingType === 'text' && (
                    <Progress value={generationProgress.text} className="h-2" />
                  )}
                </CardContent>
              </Card>
              
              {textResults.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {textResults.map((result, index) => (
                    <ComparisonCard
                      key={index}
                      result={result}
                      type="text"
                      onSelect={setSelectedText}
                      isSelected={selectedText?.provider_id === result.provider_id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Image Generation Tab */}
            <TabsContent value="image" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Image Generation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ProviderSelector
                    type="image"
                    selected={selectedImageProviders}
                    onSelect={setSelectedImageProviders}
                    providers={providers.image}
                  />
                  
                  <Button
                    onClick={generateImages}
                    disabled={loading || selectedImageProviders.length === 0}
                    className="bg-accent hover:bg-accent/90"
                    data-testid="generate-image-btn"
                  >
                    {generatingType === 'image' ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Images...
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Generate Images
                      </>
                    )}
                  </Button>
                  
                  {generatingType === 'image' && (
                    <Progress value={generationProgress.image} className="h-2" />
                  )}
                </CardContent>
              </Card>
              
              {imageResults.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {imageResults.map((result, index) => (
                    <ComparisonCard
                      key={index}
                      result={result}
                      type="image"
                      onSelect={setSelectedImage}
                      isSelected={selectedImage?.provider_id === result.provider_id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Video Generation Tab */}
            <TabsContent value="video" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Video Generation</CardTitle>
                  <CardDescription>
                    Video generation may take 2-5 minutes per provider
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ProviderSelector
                    type="video"
                    selected={selectedVideoProviders}
                    onSelect={setSelectedVideoProviders}
                    providers={providers.video}
                  />
                  
                  <Button
                    onClick={generateVideos}
                    disabled={loading || selectedVideoProviders.length === 0}
                    className="bg-accent hover:bg-accent/90"
                    data-testid="generate-video-btn"
                  >
                    {generatingType === 'video' ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Videos...
                      </>
                    ) : (
                      <>
                        <Video className="w-4 h-4 mr-2" />
                        Generate Videos
                      </>
                    )}
                  </Button>
                  
                  {generatingType === 'video' && (
                    <div className="space-y-2">
                      <Progress value={generationProgress.video} className="h-2" />
                      <p className="text-xs text-slate-500">This may take a few minutes...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {videoResults.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videoResults.map((result, index) => (
                    <ComparisonCard
                      key={index}
                      result={result}
                      type="video"
                      onSelect={setSelectedVideo}
                      isSelected={selectedVideo?.provider_id === result.provider_id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
      
      {/* Step 3: Review & Publish */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setStep(2)}
              variant="outline"
              data-testid="back-to-generation-btn"
            >
              ← Back to Generation
            </Button>
            <div className="flex-1">
              <h4 className="font-medium text-primary">Review Your Campaign</h4>
              <p className="text-sm text-slate-500">
                Finalize and publish your content
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Selected Content Preview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Text Preview */}
              {selectedText && (
                <Card data-testid="selected-text-preview">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Selected Text
                      <Badge variant="secondary">{selectedText.provider}</Badge>
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={copyContent}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                      {selectedText.content?.headline && (
                        <h4 className="font-semibold text-lg text-primary">
                          {selectedText.content.headline}
                        </h4>
                      )}
                      <p className="text-slate-700 whitespace-pre-wrap">
                        {selectedText.content?.body || selectedText.content?.text}
                      </p>
                      {selectedText.content?.cta && (
                        <p className="font-medium text-accent">
                          {selectedText.content.cta}
                        </p>
                      )}
                      {selectedText.content?.hashtags && (
                        <div className="flex flex-wrap gap-1">
                          {selectedText.content.hashtags.map((tag, i) => (
                            <Badge key={i} variant="secondary">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Image Preview */}
              {selectedImage && (
                <Card data-testid="selected-image-preview">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Selected Image
                      <Badge variant="secondary">{selectedImage.provider}</Badge>
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={downloadImage}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={`data:image/png;base64,${selectedImage.image_base64}`}
                        alt="Selected campaign image"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Video Preview */}
              {selectedVideo && (
                <Card data-testid="selected-video-preview">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Selected Video
                      <Badge variant="secondary">{selectedVideo.provider}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-900">
                      <video
                        src={selectedVideo.video_base64 
                          ? `data:video/mp4;base64,${selectedVideo.video_base64}`
                          : selectedVideo.video_url}
                        controls
                        className="w-full h-full"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {!selectedText && !selectedImage && !selectedVideo && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-slate-500">No content selected yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setStep(2)}
                    >
                      Go Back to Generate Content
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Actions Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Publishing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full bg-accent hover:bg-accent/90"
                    onClick={saveCampaign}
                    data-testid="save-campaign-btn"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Save Campaign
                  </Button>
                  
                  <Separator />
                  
                  <p className="text-sm text-slate-500">Publish to:</p>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {['instagram', 'facebook', 'linkedin', 'twitter'].map(platform => (
                      <Button
                        key={platform}
                        variant="outline"
                        size="sm"
                        className="justify-start"
                        onClick={() => toast.info(`Publishing to ${platform} coming soon!`)}
                      >
                        <PlatformIcon platform={platform} className="w-4 h-4 mr-2" />
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </Button>
                    ))}
                  </div>
                  
                  <Separator />
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => toast.info('Scheduling coming soon!')}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Post
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Campaign Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Product:</span>
                    <span className="font-medium">{brief.product || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Platform:</span>
                    <span className="font-medium capitalize">{brief.platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Type:</span>
                    <span className="font-medium capitalize">{brief.campaign_type?.replace('_', ' ')}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Text:</span>
                    <span className={selectedText ? 'text-green-600' : 'text-slate-400'}>
                      {selectedText ? '✓ Selected' : 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Image:</span>
                    <span className={selectedImage ? 'text-green-600' : 'text-slate-400'}>
                      {selectedImage ? '✓ Selected' : 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Video:</span>
                    <span className={selectedVideo ? 'text-green-600' : 'text-slate-400'}>
                      {selectedVideo ? '✓ Selected' : 'Not selected'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
      
      {/* Step 4: A/B Testing */}
      {step === 4 && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setStep(3)}
              variant="outline"
              data-testid="back-to-review-btn"
            >
              ← Back to Review
            </Button>
            <div className="flex-1">
              <h4 className="font-medium text-primary flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-accent" />
                A/B Testing Laboratory
              </h4>
              <p className="text-sm text-slate-500">
                Test different AI-generated content variants to optimize performance
              </p>
            </div>
            <Button
              onClick={() => setShowABTestDialog(true)}
              className="bg-accent hover:bg-accent/90"
              disabled={textResults.length < 2 && imageResults.length < 2}
              data-testid="create-ab-test-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create A/B Test
            </Button>
          </div>
          
          {/* Available Variants for Testing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Available Content Variants
              </CardTitle>
              <CardDescription>
                Select variants from your generated content to create A/B tests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="text">
                <TabsList>
                  <TabsTrigger value="text">
                    <FileText className="w-4 h-4 mr-2" />
                    Text ({textResults.filter(r => r.success).length})
                  </TabsTrigger>
                  <TabsTrigger value="image">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Images ({imageResults.filter(r => r.success).length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="text" className="mt-4">
                  {textResults.filter(r => r.success).length === 0 ? (
                    <p className="text-center text-slate-500 py-4">
                      Generate text content first to create A/B tests
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {textResults.filter(r => r.success).map((result, idx) => (
                        <Card key={idx} className="border-slate-200">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <ProviderIcon provider={result.provider_id} />
                                  <span className="font-medium text-sm">{result.provider}</span>
                                </div>
                                <p className="text-sm text-slate-600 line-clamp-2">
                                  {result.content?.headline || result.content?.body?.slice(0, 100)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => addVariantToABTest(result, 'text')}
                                disabled={abTestVariants.some(v => v.provider_id === result.provider_id)}
                                data-testid={`add-variant-${result.provider_id}`}
                              >
                                {abTestVariants.some(v => v.provider_id === result.provider_id) ? (
                                  <Check className="w-4 h-4" />
                                ) : (
                                  <Plus className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="image" className="mt-4">
                  {imageResults.filter(r => r.success).length === 0 ? (
                    <p className="text-center text-slate-500 py-4">
                      Generate images first to create A/B tests
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {imageResults.filter(r => r.success).map((result, idx) => (
                        <Card key={idx} className="border-slate-200 overflow-hidden">
                          <div className="aspect-square relative">
                            <img
                              src={`data:image/png;base64,${result.image_base64}`}
                              alt={`${result.provider}`}
                              className="w-full h-full object-cover"
                            />
                            <Button
                              size="sm"
                              className="absolute bottom-2 right-2"
                              onClick={() => addVariantToABTest(result, 'image')}
                              disabled={abTestVariants.some(v => v.provider_id === result.provider_id)}
                            >
                              {abTestVariants.some(v => v.provider_id === result.provider_id) ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <CardContent className="py-2">
                            <div className="flex items-center gap-2">
                              <ProviderIcon provider={result.provider_id} />
                              <span className="text-sm">{result.provider}</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              
              {/* Selected Variants Preview */}
              {abTestVariants.length > 0 && (
                <div className="mt-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-accent" />
                      Selected Variants ({abTestVariants.length}/4)
                    </h4>
                    <Button
                      size="sm"
                      onClick={() => setShowABTestDialog(true)}
                      className="bg-accent hover:bg-accent/90"
                      disabled={abTestVariants.length < 2}
                    >
                      Configure Test
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {abTestVariants.map((variant, idx) => (
                      <Badge
                        key={idx}
                        variant="secondary"
                        className="flex items-center gap-2 py-1.5 px-3"
                      >
                        <span className="font-semibold">Variant {String.fromCharCode(65 + idx)}</span>
                        <span className="text-slate-500">•</span>
                        <span>{variant.provider}</span>
                        <button
                          onClick={() => removeVariantFromABTest(idx)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Active A/B Tests */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Your A/B Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {abTests.length === 0 ? (
                <div className="text-center py-8">
                  <FlaskConical className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No A/B tests yet</p>
                  <p className="text-sm text-slate-400">
                    Select variants above and create your first test
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {abTests.map((test) => (
                    <Card key={test.test_id} className="border-slate-200">
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{test.name}</h4>
                              <Badge
                                className={
                                  test.status === 'running' ? 'bg-green-500' :
                                  test.status === 'completed' ? 'bg-blue-500' :
                                  test.status === 'paused' ? 'bg-yellow-500' :
                                  'bg-slate-500'
                                }
                              >
                                {test.status}
                              </Badge>
                              {test.winner && (
                                <Badge className="bg-amber-500">
                                  <Trophy className="w-3 h-3 mr-1" />
                                  Winner: {test.winner}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">
                              {test.variants?.length || 0} variants • Goal: {test.goal} • {test.platform}
                            </p>
                            {test.statistical_significance > 0 && (
                              <p className="text-sm text-accent mt-1">
                                {test.statistical_significance}% confidence
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {test.status === 'draft' && (
                              <Button
                                size="sm"
                                onClick={() => startABTest(test.test_id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Start
                              </Button>
                            )}
                            {test.status === 'running' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => pauseABTest(test.test_id)}
                                >
                                  <Pause className="w-4 h-4 mr-1" />
                                  Pause
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => endABTest(test.test_id)}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  End
                                </Button>
                              </>
                            )}
                            {test.status === 'paused' && (
                              <Button
                                size="sm"
                                onClick={() => startABTest(test.test_id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Resume
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewABTestAnalytics(test.test_id)}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteABTest(test.test_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Variant Performance Preview */}
                        {test.variants && test.variants.length > 0 && (
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {test.variants.map((variant, vIdx) => (
                              <div
                                key={vIdx}
                                className={`p-3 rounded-lg border ${
                                  variant.variant_id === test.winner 
                                    ? 'border-amber-400 bg-amber-50' 
                                    : 'border-slate-200 bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-sm">{variant.name}</span>
                                  <span className="text-xs text-slate-500">{variant.traffic_percentage}%</span>
                                </div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Impressions</span>
                                    <span>{variant.metrics?.impressions || 0}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">CTR</span>
                                    <span className="font-medium">{variant.metrics?.ctr || 0}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Conversions</span>
                                    <span>{variant.metrics?.conversions || 0}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Create A/B Test Dialog */}
      <Dialog open={showABTestDialog} onOpenChange={setShowABTestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-accent" />
              Create A/B Test
            </DialogTitle>
            <DialogDescription>
              Configure your test to compare content variants
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Test Name</Label>
              <Input
                value={abTestName}
                onChange={(e) => setAbTestName(e.target.value)}
                placeholder="e.g., Q1 Campaign Headline Test"
                data-testid="ab-test-name-input"
              />
            </div>
            
            <div>
              <Label>Optimization Goal</Label>
              <Select value={abTestGoal} onValueChange={setAbTestGoal}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clicks">Click-Through Rate (CTR)</SelectItem>
                  <SelectItem value="conversions">Conversion Rate</SelectItem>
                  <SelectItem value="engagement">Engagement Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="mb-3 block">Selected Variants ({abTestVariants.length})</Label>
              {abTestVariants.length < 2 ? (
                <div className="p-4 border border-dashed border-slate-300 rounded-lg text-center text-slate-500">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                  Select at least 2 variants to create a test
                </div>
              ) : (
                <div className="space-y-2">
                  {abTestVariants.map((variant, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Badge className="bg-accent">{String.fromCharCode(65 + idx)}</Badge>
                        <span className="text-sm">{variant.provider}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Percent className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium w-8 text-right">
                            {trafficSplit[idx] || 0}%
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeVariantFromABTest(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {abTestVariants.length >= 2 && (
              <div>
                <Label className="mb-3 block">Traffic Split</Label>
                <div className="flex items-center gap-4">
                  {abTestVariants.map((_, idx) => (
                    <div key={idx} className="flex-1 text-center">
                      <Badge className={idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-green-500' : idx === 2 ? 'bg-purple-500' : 'bg-orange-500'}>
                        {String.fromCharCode(65 + idx)}: {trafficSplit[idx]}%
                      </Badge>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Traffic is split evenly by default
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowABTestDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={createABTest}
              disabled={loading || !abTestName || abTestVariants.length < 2}
              className="bg-accent hover:bg-accent/90"
              data-testid="confirm-create-ab-test-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FlaskConical className="w-4 h-4 mr-2" />
              )}
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* A/B Test Analytics Dialog */}
      <Dialog open={showABTestAnalytics} onOpenChange={setShowABTestAnalytics}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent" />
              A/B Test Analytics
            </DialogTitle>
          </DialogHeader>
          
          {selectedABTest && (
            <div className="space-y-6 py-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="py-4 text-center">
                    <p className="text-2xl font-bold">
                      {selectedABTest.analytics?.summary?.total_impressions || 0}
                    </p>
                    <p className="text-sm text-slate-500">Impressions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4 text-center">
                    <p className="text-2xl font-bold">
                      {selectedABTest.analytics?.summary?.total_clicks || 0}
                    </p>
                    <p className="text-sm text-slate-500">Clicks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4 text-center">
                    <p className="text-2xl font-bold text-accent">
                      {selectedABTest.analytics?.summary?.avg_ctr || 0}%
                    </p>
                    <p className="text-sm text-slate-500">Avg CTR</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {selectedABTest.test?.statistical_significance || 0}%
                    </p>
                    <p className="text-sm text-slate-500">Confidence</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Variant Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Variant Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {selectedABTest.analytics?.variants?.map((variant, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={variant.is_winner ? 'bg-amber-500' : 'bg-slate-500'}>
                              {variant.name}
                            </Badge>
                            {variant.is_winner && (
                              <Trophy className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          <span className="font-semibold">{variant.ctr}% CTR</span>
                        </div>
                        <div className="flex gap-2 h-2">
                          <div 
                            className="bg-blue-500 rounded-full transition-all"
                            style={{ width: `${Math.min(variant.ctr * 10, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>{variant.impressions} impressions</span>
                          <span>{variant.clicks} clicks</span>
                          <span>{variant.conversions} conversions</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Recommendations */}
              {selectedABTest.analytics?.recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      AI Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedABTest.analytics.recommendations.map((rec, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg ${
                            rec.type === 'winner' ? 'bg-green-50 border border-green-200' :
                            rec.type === 'promising' ? 'bg-blue-50 border border-blue-200' :
                            'bg-slate-50 border border-slate-200'
                          }`}
                        >
                          <p className="text-sm">{rec.message}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowABTestAnalytics(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Previous Campaign</DialogTitle>
            <DialogDescription>
              Select a campaign to import and enhance
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-96">
            {savedCampaigns.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No saved campaigns found
              </div>
            ) : (
              <div className="space-y-2">
                {savedCampaigns.map((campaign, index) => (
                  <Card 
                    key={index}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => importCampaign(campaign)}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{campaign.name || 'Untitled Campaign'}</p>
                          <p className="text-sm text-slate-500">
                            {campaign.brief?.product} • {campaign.brief?.platform}
                          </p>
                        </div>
                        <Badge variant="secondary">
                          {campaign.status || 'draft'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AICampaignCreator360;
